import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import config from '../config';

const API = config.API_URL;

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'received', label: 'Received' },
  { key: 'in_progress', label: 'In repair' },
  { key: 'ready', label: 'Ready' },
  { key: 'delivered', label: 'Delivered' },
];

const MOBILE_FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'received', label: 'New' },
  { key: 'in_progress', label: 'Repairing' },
  { key: 'ready', label: 'Ready' },
  { key: 'delivered', label: 'Done' },
];

const ISSUE_OPTIONS = [
  { value: 'screen', label: 'Screen' },
  { value: 'battery', label: 'Battery' },
  { value: 'charging', label: 'Charging' },
  { value: 'speaker', label: 'Speaker' },
  { value: 'camera', label: 'Camera' },
  { value: 'software', label: 'Software' },
  { value: 'water_damage', label: 'Water damage' },
  { value: 'back_glass', label: 'Back glass' },
  { value: 'mic', label: 'Mic' },
  { value: 'other', label: 'Other' },
];

const MAIN_ISSUE_OPTIONS = ISSUE_OPTIONS.filter((o) => o.value !== 'other');

const ISSUE_ICONS = {
  screen: 'bi-phone',
  battery: 'bi-battery-half',
  charging: 'bi-lightning-charge',
  speaker: 'bi-volume-up',
  camera: 'bi-camera',
  software: 'bi-code-slash',
  water_damage: 'bi-droplet',
  back_glass: 'bi-phone-flip',
  mic: 'bi-mic',
  other: 'bi-three-dots',
};

const STATUS_BADGE = {
  received: 'bg-primary',
  diagnosing: 'bg-info text-dark',
  in_progress: 'bg-warning text-dark',
  waiting_parts: 'bg-secondary',
  ready: 'bg-success',
  delivered: 'bg-dark',
  cancelled: 'bg-danger',
};

const STATUS_LABELS = {
  received: 'Received',
  diagnosing: 'Diagnosing',
  in_progress: 'In repair',
  waiting_parts: 'Waiting parts',
  ready: 'Ready',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
};

const EMPTY_FORM = {
  customerPhone: '',
  customerName: '',
  customerAddress: '',
  deviceBrand: '',
  deviceModel: '',
  issueCategory: 'screen',
  issueCategories: ['screen'],
  issueDescription: '',
  estimatedCost: '',
  advancePaid: '',
  intakeNotes: '',
  imei: '',
  priority: 'normal',
  deviceTier: 'auto',
};

const DEVICE_TIER_OPTIONS = [
  { value: 'auto', label: 'Auto detect' },
  { value: 'iphone', label: 'iPhone / Apple' },
  { value: 'premium', label: 'Premium Android' },
  { value: 'standard', label: 'Standard smartphone' },
  { value: 'budget', label: 'Budget / Keypad' },
];

function formatINR(n) {
  const v = Number(n);
  if (!v) return '—';
  return `₹${v.toLocaleString('en-IN')}`;
}

function displayStatus(status) {
  if (['in_progress', 'waiting_parts', 'diagnosing'].includes(status)) return 'In repair';
  return STATUS_LABELS[status] || status;
}

function jobCardStatusClass(status) {
  if (status === 'received') return 'status-received';
  if (['in_progress', 'waiting_parts', 'diagnosing'].includes(status)) return 'status-repairing';
  if (status === 'ready') return 'status-ready';
  if (status === 'delivered') return 'status-delivered';
  if (status === 'cancelled') return 'status-cancelled';
  return '';
}

function issueLabel(cat) {
  return ISSUE_OPTIONS.find((o) => o.value === cat)?.label || cat;
}

function normalizeIssueCategories(raw) {
  if (!Array.isArray(raw)) return [];
  const valid = new Set(ISSUE_OPTIONS.map((o) => o.value));
  return Array.from(new Set(raw.map((x) => String(x || '').trim()).filter((x) => valid.has(x))));
}

function getPrimaryIssueCategory(issueCategories) {
  const list = normalizeIssueCategories(issueCategories);
  const firstMain = list.find((x) => x !== 'other');
  return firstMain || list[0] || 'other';
}

function RepairsDashboard({ mobileApp = false, onStatsChange, refreshTick = 0 }) {
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState(mobileApp ? 'received' : 'all');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterIssue, setFilterIssue] = useState('all');
  const [mobileTab, setMobileTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [photos, setPhotos] = useState([]);
  const [lookup, setLookup] = useState(null);
  const [modelSuggestions, setModelSuggestions] = useState([]);
  const [showModelSuggest, setShowModelSuggest] = useState(false);
  const [modelSuggestLoading, setModelSuggestLoading] = useState(false);
  const [modelSource, setModelSource] = useState('manual');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [selected, setSelected] = useState(null);
  const [editAdvance, setEditAdvance] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [inventoryParts, setInventoryParts] = useState([]);
  const [finishPreview, setFinishPreview] = useState(null);
  const [showFinish, setShowFinish] = useState(false);
  const [finishFinal, setFinishFinal] = useState('');
  const [finishAdvance, setFinishAdvance] = useState('');
  const [finishPartLines, setFinishPartLines] = useState([{ partId: '', quantity: 1 }]);
  const [finishManualLabor, setFinishManualLabor] = useState('');
  const [partSearch, setPartSearch] = useState('');
  const [finishing, setFinishing] = useState(false);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4500);
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/repairs/stats`);
      setStats(res.data);
      onStatsChange?.();
    } catch (e) {
      console.error(e);
    }
  }, [onStatsChange]);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);
      if (search.trim()) params.set('q', search.trim());
      if (filterIssue !== 'all') params.set('issueCategory', filterIssue);
      const res = await axios.get(`${API}/repairs?${params}`);
      setJobs(res.data || []);
    } catch (e) {
      showToast(e?.response?.data?.message || 'Load failed', 'danger');
    } finally {
      setLoading(false);
    }
  }, [filter, search, filterIssue]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    if (refreshTick > 0) {
      loadStats();
      loadJobs();
    }
  }, [refreshTick, loadStats, loadJobs]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const digits = form.customerPhone.replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) {
      setLookup(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/repairs/lookup/phone/${digits}`);
        setLookup(res.data);
        if (res.data.found && res.data.customer) {
          setForm((f) => ({
            ...f,
            customerName: res.data.customer.name || f.customerName,
            customerAddress: res.data.customer.address || f.customerAddress,
          }));
        }
      } catch {
        setLookup(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [form.customerPhone]);

  useEffect(() => {
    const q = form.deviceModel.trim();
    if (q.length < 2) {
      setModelSuggestions([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      setModelSuggestLoading(true);
      try {
        const res = await axios.get(`${API}/repairs/meta/device-suggest?q=${encodeURIComponent(q)}`);
        setModelSuggestions(res.data?.suggestions || []);
      } catch {
        setModelSuggestions([]);
      } finally {
        setModelSuggestLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.deviceModel, form.deviceBrand]);

  const applyModelSuggestion = (s) => {
    setForm((f) => ({
      ...f,
      deviceBrand: s.deviceBrand || f.deviceBrand,
      deviceModel: s.deviceModel || s.label || f.deviceModel,
      deviceTier: f.deviceTier === 'auto' && s.deviceTier ? s.deviceTier : f.deviceTier,
    }));
    setModelSource('suggested');
    setShowModelSuggest(false);
    setModelSuggestions([]);
  };

  const normalizeModelOnBlur = async () => {
    if (modelSource !== 'suggested') return;
    const q = form.deviceModel.trim();
    if (q.length < 3) return;
    try {
      const res = await axios.post(`${API}/repairs/meta/normalize-device`, {
        query: q,
        brand: form.deviceBrand,
      });
      if (res.data?.deviceModel) {
        setForm((f) => ({
          ...f,
          deviceBrand: res.data.deviceBrand || f.deviceBrand,
          deviceModel: res.data.deviceModel,
          deviceTier: f.deviceTier === 'auto' && res.data.deviceTier ? res.data.deviceTier : f.deviceTier,
        }));
      }
    } catch {
      /* keep typed value */
    }
  };

  const onFormChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const selectedIssues = normalizeIssueCategories(form.issueCategories);
  const hasOtherProblem = selectedIssues.includes('other');
  const onlyOtherProblem = hasOtherProblem && selectedIssues.length === 1;

  const toggleProblem = (value) => {
    setForm((f) => ({
      ...f,
      issueCategories: (() => {
        const cur = normalizeIssueCategories(f.issueCategories);
        if (cur.includes(value)) {
          const next = cur.filter((x) => x !== value);
          return next.length ? next : ['other'];
        }
        return [...cur, value];
      })(),
      issueCategory: (() => {
        const cur = normalizeIssueCategories(f.issueCategories);
        const next = cur.includes(value)
          ? (cur.filter((x) => x !== value).length ? cur.filter((x) => x !== value) : ['other'])
          : [...cur, value];
        return getPrimaryIssueCategory(next);
      })(),
    }));
  };

  const toggleOtherProblem = () => toggleProblem('other');

  const onIssueDetailChange = (value) => {
    setForm((f) => ({ ...f, issueDescription: value }));
  };

  const renderProblemPicker = (compact = false) => (
    <>
      <div className={`repair-problem-chips ${compact ? '' : 'mb-2'}`}>
        {MAIN_ISSUE_OPTIONS.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`repair-problem-chip ${selectedIssues.includes(o.value) ? 'active' : ''}`}
            onClick={() => toggleProblem(o.value)}
          >
            <i className={`bi ${ISSUE_ICONS[o.value] || 'bi-circle'} me-1`} />
            {o.label}
          </button>
        ))}
        <button
          type="button"
          className={`repair-problem-chip repair-problem-chip-other ${hasOtherProblem ? 'active' : ''}`}
          onClick={toggleOtherProblem}
        >
          <i className="bi bi-pencil me-1" />
          Other
        </button>
      </div>
      {hasOtherProblem && (
        <div className={`alert alert-warning py-2 ${compact ? 'small mb-2' : 'small mb-2'}`}>
          {onlyOtherProblem
            ? <>Unique problem only — finish par <strong>manual labour</strong> likhni hogi (fixed rate nahi).</>
            : <>Multiple problems selected — labour primary issue ke hisaab se lagega.</>}
        </div>
      )}
      <div className={compact ? 'mb-2' : 'mb-2'}>
        <label className={`form-label ${compact ? 'small mb-0' : 'small mb-0'}`}>
          {onlyOtherProblem ? 'Problem detail *' : 'Issue detail'}
        </label>
        <input
          className={compact ? 'form-control form-control-sm' : 'form-control form-control-sm'}
          placeholder={onlyOtherProblem ? 'Unique problem likho…' : 'Screen crack, battery drain…'}
          value={form.issueDescription}
          onChange={(e) => onIssueDetailChange(e.target.value)}
          required={onlyOtherProblem}
        />
      </div>
    </>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    const phone = form.customerPhone.replace(/\D/g, '').slice(-10);
    if (phone.length !== 10) {
      showToast('10-digit phone number zaroori hai', 'warning');
      return;
    }
    if (!form.deviceModel.trim()) {
      showToast('Device model likho', 'warning');
      return;
    }
    if (!form.customerName.trim() && !lookup?.found) {
      showToast('Naye customer ke liye naam likho', 'warning');
      return;
    }

    if (selectedIssues.length < 1) {
      showToast('Kam se kam 1 problem select karo', 'warning');
      return;
    }
    if (onlyOtherProblem && !form.issueDescription.trim()) {
      showToast('Other problem ke liye detail likho', 'warning');
      return;
    }

    if (lookup?.hasOpenRepair && !window.confirm(
      `Is number par pehle se ${lookup.openRepairs.length} open repair hai (${lookup.openRepairs.map((r) => r.jobNumber).join(', ')}). Phir bhi naya job banayein?`
    )) {
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      const primaryIssue = getPrimaryIssueCategory(selectedIssues);
      const payloadForm = {
        ...form,
        issueCategory: primaryIssue,
      };
      Object.entries(payloadForm).forEach(([k, v]) => {
        if (k === 'issueCategories') return;
        fd.append(k, v ?? '');
      });
      fd.set('customerPhone', phone);
      fd.set('issueCategories', JSON.stringify(selectedIssues));
      fd.set('skipAiNormalize', modelSource === 'manual' ? 'true' : 'false');
      photos.forEach((f) => fd.append('photos', f));

      const res = await axios.post(`${API}/repairs`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(
        `${res.data.job?.jobNumber || 'Job'} save ho gaya — WhatsApp pickup ke baad jayega`,
        'success'
      );
      setForm(EMPTY_FORM);
      setModelSource('manual');
      setPhotos([]);
      setLookup(null);
      if (mobileApp) {
        setMobileTab('jobs');
        setFilter('received');
      }
      loadStats();
      loadJobs();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Submit failed', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const openDetail = async (job) => {
    setSelected(job);
    setEditAdvance(String(job.advancePaid || ''));
    setEditNotes(job.internalNotes || '');
    setShowFinish(false);
    try {
      const previewRes = await axios.get(`${API}/repairs/${job._id}/finish-preview`);
      setFinishPreview(previewRes.data);
      setInventoryParts(previewRes.data?.parts || []);
    } catch {
      setInventoryParts([]);
      setFinishPreview(null);
    }
  };

  const filteredParts = useMemo(() => {
    const q = partSearch.trim().toLowerCase();
    let list = inventoryParts || [];
    if (q) {
      list = list.filter((p) =>
        `${p.name} ${p.compatibleModels} ${p.sku}`.toLowerCase().includes(q)
      );
    }
    return list;
  }, [inventoryParts, partSearch]);

  const suggestedParts = useMemo(
    () => (inventoryParts || []).filter((p) => p.suggested && p.quantity > 0).slice(0, 8),
    [inventoryParts]
  );

  const pickSuggestedPart = (partId) => {
    const emptyIdx = finishPartLines.findIndex((l) => !l.partId);
    if (emptyIdx >= 0) {
      const next = [...finishPartLines];
      next[emptyIdx] = { ...next[emptyIdx], partId };
      setFinishPartLines(next);
    } else {
      setFinishPartLines([...finishPartLines, { partId, quantity: 1 }]);
    }
  };

  const openFinishModal = () => {
    if (!selected) return;
    setFinishFinal(String(selected.finalCost || selected.estimatedCost || ''));
    setFinishAdvance(String(selected.advancePaid || ''));
    setFinishPartLines([{ partId: '', quantity: 1 }]);
    setPartSearch('');
    setFinishManualLabor(
      selected.issueCategory === 'other' ? String(finishPreview?.laborCost || '') : ''
    );
    setShowFinish(true);
  };

  const reloadPartsSearch = async (q) => {
    if (!selected) return;
    try {
      const params = new URLSearchParams({
        deviceBrand: selected.deviceBrand || '',
        deviceModel: selected.deviceModel || '',
        q: q || '',
      });
      const res = await axios.get(`${API}/repair-parts?${params}`);
      setInventoryParts(res.data?.parts || []);
    } catch {
      /* keep existing list */
    }
  };

  const submitFinish = async () => {
    if (!selected) return;
    setFinishing(true);
    try {
      const partsUsed = finishPartLines
        .filter((l) => l.partId)
        .map((l) => ({ partId: l.partId, quantity: Number(l.quantity) || 1 }));
      const res = await axios.post(`${API}/repairs/${selected._id}/finish`, {
        finalCost: Number(finishFinal),
        advancePaid: Number(finishAdvance) || 0,
        partsUsed,
        ...(selected.issueCategory === 'other' ? { laborCost: Number(finishManualLabor) } : {}),
      });
      setSelected(res.data.job);
      setShowFinish(false);
      showToast('Repair finish — parts stock cut ho gaya', 'success');
      loadStats();
      loadJobs();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Finish failed', 'danger');
    } finally {
      setFinishing(false);
    }
  };

  const submitDeliver = async () => {
    if (!selected) return;
    setUpdating(true);
    try {
      const res = await axios.post(`${API}/repairs/${selected._id}/deliver`, {
        advancePaid: Number(editAdvance) || 0,
        paymentStatus: 'paid',
      });
      setSelected(res.data.job);
      showToast('Delivered — customer ne le liya', 'success');
      loadStats();
      loadJobs();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Deliver failed', 'danger');
    } finally {
      setUpdating(false);
    }
  };

  const updateJob = async (patch) => {
    if (!selected) return;
    setUpdating(true);
    try {
      const res = await axios.patch(`${API}/repairs/${selected._id}`, patch);
      setSelected(res.data.job);
      showToast(res.data.message || 'Updated', 'success');
      loadStats();
      loadJobs();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Update failed', 'danger');
    } finally {
      setUpdating(false);
    }
  };

  const saveNotes = () => {
    updateJob({ internalNotes: editNotes, advancePaid: Number(editAdvance) || 0 });
  };

  const readyCount = useMemo(() => stats?.readyForPickup || 0, [stats]);
  const activeCount = useMemo(() => stats?.activeJobs ?? 0, [stats]);

  const mobileHome = (
    <div>
      <div className="repair-home-greeting">
        {(() => {
          const h = new Date().getHours();
          if (h < 12) return 'Good morning';
          if (h < 17) return 'Good afternoon';
          return 'Good evening';
        })()}
        , counter ready
      </div>
      <div className="repair-home-sub">Aaj ka repair overview</div>

      <div className="repair-stat-grid">
        <div className="repair-stat-card accent-blue">
          <div className="stat-icon"><i className="bi bi-wrench" /></div>
          <div className="stat-value">{activeCount}</div>
          <div className="stat-label">Active repairs</div>
        </div>
        <div className="repair-stat-card accent-green">
          <div className="stat-icon"><i className="bi bi-bell" /></div>
          <div className="stat-value">{readyCount}</div>
          <div className="stat-label">Ready pickup</div>
        </div>
        <div className="repair-stat-card accent-orange">
          <div className="stat-icon"><i className="bi bi-check-circle" /></div>
          <div className="stat-value">{stats?.deliveredThisMonth ?? '—'}</div>
          <div className="stat-label">Delivered (month)</div>
        </div>
        <div className="repair-stat-card accent-purple">
          <div className="stat-icon"><i className="bi bi-clock" /></div>
          <div className="stat-value">{stats?.avgTurnaroundDays ?? '—'}</div>
          <div className="stat-label">Avg days</div>
        </div>
      </div>

      <div className="repair-quick-actions">
        <button type="button" className="repair-quick-btn primary" onClick={() => setMobileTab('new')}>
          <i className="bi bi-plus-circle" />
          Naya repair
        </button>
        <button
          type="button"
          className="repair-quick-btn success"
          onClick={() => { setFilter('ready'); setMobileTab('jobs'); }}
        >
          <i className="bi bi-bag-check" />
          Ready jobs {readyCount > 0 ? `(${readyCount})` : ''}
        </button>
      </div>

      {readyCount > 0 && (
        <div className="repair-section-card">
          <div className="repair-section-head">
            <i className="bi bi-exclamation-circle" />
            {readyCount} phone pickup ke liye ready
          </div>
          <div className="repair-section-body py-2">
            <button
              type="button"
              className="btn btn-success w-100"
              onClick={() => { setFilter('ready'); setMobileTab('jobs'); }}
            >
              Ready list dekho
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const mobileIntakeForm = (
    <form onSubmit={handleSubmit}>
      <div className="repair-section-card">
        <div className="repair-section-head"><i className="bi bi-person" /> Customer</div>
        <div className="repair-section-body">
          <div className="mb-3">
            <label className="form-label">Phone number *</label>
            <input
              className="form-control"
              placeholder="10 digit mobile"
              value={form.customerPhone}
              onChange={(e) => onFormChange('customerPhone', e.target.value)}
              inputMode="tel"
              maxLength={14}
              required
            />
            {lookup?.found && <div className="small text-success mt-1">✓ {lookup.customer.name}</div>}
            {lookup && !lookup.found && form.customerPhone.replace(/\D/g, '').length === 10 && (
              <div className="small text-info mt-1">Naya customer</div>
            )}
            {lookup?.hasOpenRepair && (
              <div className="alert alert-warning py-2 small mt-2 mb-0">
                <strong>⚠ Pehle se open repair hai</strong>
                <ul className="mb-0 ps-3 mt-1">
                  {lookup.openRepairs.map((r) => (
                    <li key={r.jobNumber}><code>{r.jobNumber}</code> — {displayStatus(r.status)}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="mb-0">
            <label className="form-label">Name {lookup?.found ? '' : '*'}</label>
            <input className="form-control" value={form.customerName} onChange={(e) => onFormChange('customerName', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="repair-section-card">
        <div className="repair-section-head"><i className="bi bi-phone" /> Device</div>
        <div className="repair-section-body">
          <div className="row g-2 mb-3">
            <div className="col-5">
              <label className="form-label">Brand</label>
              <input className="form-control" placeholder="Samsung" value={form.deviceBrand} onChange={(e) => onFormChange('deviceBrand', e.target.value)} />
            </div>
            <div className="col-7 position-relative">
              <label className="form-label">Model *</label>
              <input
                className="form-control"
                placeholder="iPhone 11, Redmi Note 10…"
                value={form.deviceModel}
                onChange={(e) => { onFormChange('deviceModel', e.target.value); setModelSource('manual'); setShowModelSuggest(true); }}
                onFocus={() => setShowModelSuggest(true)}
                onBlur={() => { setTimeout(() => setShowModelSuggest(false), 200); normalizeModelOnBlur(); }}
                required
                autoComplete="off"
              />
              {showModelSuggest && (modelSuggestions.length > 0 || modelSuggestLoading) && (
                <div className="repair-model-suggest">
                  {modelSuggestLoading && <div className="px-3 py-2 small text-muted">Searching…</div>}
                  {modelSuggestions.map((s) => (
                    <button
                      key={s.deviceModelKey || s.label}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={() => applyModelSuggestion(s)}
                    >
                      <strong>{s.deviceModelNormalized || s.label}</strong>
                      {s.repairCount > 0 && <span className="text-muted ms-1">· {s.repairCount}×</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="mb-0">
            <label className="form-label">Phone type</label>
            <select className="form-select" value={form.deviceTier} onChange={(e) => onFormChange('deviceTier', e.target.value)}>
              {DEVICE_TIER_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="repair-section-card">
        <div className="repair-section-head"><i className="bi bi-exclamation-triangle" /> Problem</div>
        <div className="repair-section-body">
          {renderProblemPicker(true)}
          <div className="mb-0">
            <label className="form-label">Estimate ₹</label>
            <input type="number" className="form-control" min="0" value={form.estimatedCost} onChange={(e) => onFormChange('estimatedCost', e.target.value)} />
          </div>
        </div>
      </div>

      <div className="repair-section-card">
        <div className="repair-section-head"><i className="bi bi-wallet2" /> Optional</div>
        <div className="repair-section-body">
          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label">Advance ₹</label>
              <input type="number" className="form-control" min="0" value={form.advancePaid} onChange={(e) => onFormChange('advancePaid', e.target.value)} />
            </div>
            <div className="col-6">
              <label className="form-label">IMEI</label>
              <input className="form-control" value={form.imei} onChange={(e) => onFormChange('imei', e.target.value)} />
            </div>
          </div>
          <div className="mb-0">
            <label className="form-label">Photos</label>
            <input type="file" className="form-control" accept="image/*" capture="environment" multiple onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 4))} />
          </div>
        </div>
      </div>

      <button type="submit" className="repair-submit-btn" disabled={submitting}>
        {submitting ? 'Saving…' : '✓ Submit + WhatsApp bhejo'}
      </button>
    </form>
  );

  const intakeForm = (
    <div className={mobileApp ? '' : 'col-lg-5'}>
      <div className="card border-primary">
        <div className="card-header bg-primary text-white py-2">
          <strong><i className="bi bi-plus-circle me-1" />Naya repair</strong>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-2">
              <label className="form-label small mb-0">Phone *</label>
              <input
                className="form-control form-control-sm"
                placeholder="10 digit mobile"
                value={form.customerPhone}
                onChange={(e) => onFormChange('customerPhone', e.target.value)}
                inputMode="tel"
                maxLength={14}
                required
              />
              {lookup?.found && (
                <div className="small text-success mt-1">
                  ✓ {lookup.customer.name}
                  {!mobileApp && (
                    <>
                      {' '}— Buy {lookup.stats.purchaseCount} · Sell {lookup.stats.soldCount} · Witness {lookup.stats.witnessCount} · Repair {lookup.stats.repairCount}
                      {' '}<Link to={`/customer/${lookup.customer._id}`}>Profile</Link>
                    </>
                  )}
                </div>
              )}
              {lookup && !lookup.found && form.customerPhone.replace(/\D/g, '').length === 10 && (
                <div className="small text-info mt-1">Naya customer — naam likho</div>
              )}
              {lookup?.hasOpenRepair && (
                <div className="alert alert-warning py-2 px-2 small mt-2 mb-0">
                  <strong>⚠ Open repair already:</strong>
                  <ul className="mb-0 ps-3 mt-1">
                    {lookup.openRepairs.map((r) => (
                      <li key={r.jobNumber}>
                        <code>{r.jobNumber}</code> — {[r.deviceBrand, r.deviceModel].filter(Boolean).join(' ') || 'Device'}{' '}
                        <span className="badge bg-secondary">{STATUS_LABELS[r.status] || r.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="row g-2 mb-2">
              <div className="col-7">
                <label className="form-label small mb-0">Name {lookup?.found ? '' : '*'}</label>
                <input className="form-control form-control-sm" value={form.customerName} onChange={(e) => onFormChange('customerName', e.target.value)} />
              </div>
              <div className="col-5">
                <label className="form-label small mb-0">Brand</label>
                <input className="form-control form-control-sm" placeholder="Samsung" value={form.deviceBrand} onChange={(e) => onFormChange('deviceBrand', e.target.value)} />
              </div>
            </div>
            <div className="mb-2 position-relative">
              <label className="form-label small mb-0">Model * {!mobileApp && <span className="text-muted">(type → suggest)</span>}</label>
              <input
                className="form-control form-control-sm"
                placeholder="iPhone 11, Redmi Note 10…"
                value={form.deviceModel}
                onChange={(e) => {
                  onFormChange('deviceModel', e.target.value);
                  setModelSource('manual');
                  setShowModelSuggest(true);
                }}
                onFocus={() => setShowModelSuggest(true)}
                onBlur={() => {
                  setTimeout(() => setShowModelSuggest(false), 200);
                  normalizeModelOnBlur();
                }}
                required
                autoComplete="off"
              />
              {showModelSuggest && (modelSuggestions.length > 0 || modelSuggestLoading) && (
                <div
                  className="list-group position-absolute w-100 shadow-sm"
                  style={{ zIndex: 1050, maxHeight: 220, overflowY: 'auto' }}
                >
                  {modelSuggestLoading && (
                    <div className="list-group-item small text-muted py-1">Searching…</div>
                  )}
                  {modelSuggestions.map((s) => (
                    <button
                      key={s.deviceModelKey || s.label}
                      type="button"
                      className="list-group-item list-group-item-action py-2 small text-start"
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={() => applyModelSuggestion(s)}
                    >
                      <strong>{s.deviceModelNormalized || s.label}</strong>
                      {s.repairCount > 0 && <span className="text-muted ms-1">· {s.repairCount}×</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mb-2">
              <label className="form-label small mb-0">Phone type</label>
              <select className="form-select form-select-sm" value={form.deviceTier} onChange={(e) => onFormChange('deviceTier', e.target.value)}>
                {DEVICE_TIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label small mb-0">Problem</label>
              {renderProblemPicker(true)}
            </div>
            <div className="row g-2 mb-2">
              <div className="col-12">
                <label className="form-label small mb-0">Estimate ₹</label>
                <input type="number" className="form-control form-control-sm" min="0" value={form.estimatedCost} onChange={(e) => onFormChange('estimatedCost', e.target.value)} />
              </div>
            </div>
            <div className="row g-2 mb-2">
              <div className="col-6">
                <label className="form-label small mb-0">Advance ₹</label>
                <input type="number" className="form-control form-control-sm" min="0" value={form.advancePaid} onChange={(e) => onFormChange('advancePaid', e.target.value)} />
              </div>
              <div className="col-6">
                <label className="form-label small mb-0">IMEI</label>
                <input className="form-control form-control-sm" value={form.imei} onChange={(e) => onFormChange('imei', e.target.value)} />
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label small mb-0">Photos</label>
              <input type="file" className="form-control form-control-sm" accept="image/*" capture="environment" multiple onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 4))} />
            </div>
            <button type="submit" className={`btn btn-primary w-100 ${mobileApp ? '' : 'btn-sm'}`} disabled={submitting}>
              {submitting ? 'Saving…' : 'Submit + WhatsApp'}
            </button>
            {!mobileApp && (
              <p className="small text-muted mt-2 mb-0">WhatsApp received template se auto jayega.</p>
            )}
          </form>
        </div>
      </div>
    </div>
  );

  const jobsList = (
    <div className={mobileApp ? '' : 'col-lg-7'}>
      <div className={mobileApp ? '' : 'card'}>
        {!mobileApp && (
          <div className="card-header py-2">
            <div className="nav nav-pills nav-fill flex-grow-1" style={{ maxWidth: '100%' }}>
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  className={`nav-link py-1 px-2 small ${filter === t.key ? 'active' : ''}`}
                  onClick={() => setFilter(t.key)}
                >
                  {t.label}
                  {t.key === 'ready' && readyCount > 0 ? ` (${readyCount})` : ''}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className={mobileApp ? '' : 'card-body p-2'}>
          {mobileApp && (
            <div className="repair-jobs-toolbar">
              <div className="repair-search-wrap">
                <i className="bi bi-search" />
                <input
                  placeholder="Job no, phone, model…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <div className="repair-filter-chips">
                {MOBILE_FILTER_TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`repair-filter-chip ${filter === t.key ? 'active' : ''}`}
                    onClick={() => setFilter(t.key)}
                  >
                    {t.label}
                    {t.key === 'ready' && readyCount > 0 && (
                      <span className="chip-count">{readyCount}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!mobileApp && (
            <div className="row g-2 mb-2">
              <div className="col-md-8">
                <input
                  className="form-control form-control-sm"
                  placeholder="Search job / phone / model…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <select className="form-select form-select-sm" value={filterIssue} onChange={(e) => setFilterIssue(e.target.value)}>
                  <option value="all">All problems</option>
                  {ISSUE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-center py-4 text-muted">Loading…</div>
          ) : jobs.length === 0 ? (
            <div className={mobileApp ? 'repair-empty-state' : 'text-center py-4 text-muted'}>
              {mobileApp && <i className="bi bi-inbox" />}
              Koi job nahi
            </div>
          ) : mobileApp ? (
            <div>
              {jobs.map((j) => (
                <button
                  key={j._id}
                  type="button"
                  className={`repair-job-card ${jobCardStatusClass(j.status)}`}
                  onClick={() => openDetail(j)}
                >
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div className="flex-grow-1">
                      <div className="job-device">{j.deviceModelNormalized || [j.deviceBrand, j.deviceModel].filter(Boolean).join(' ') || '—'}</div>
                      <div className="job-customer">{j.customerName} · {j.customerPhone}</div>
                      <div className="job-meta">
                        <span className={`repair-job-pill status ${j.status === 'ready' ? 'status-ready' : ['in_progress', 'waiting_parts', 'diagnosing'].includes(j.status) ? 'status-repairing' : ''}`}>
                          {displayStatus(j.status)}
                        </span>
                        <span className="repair-job-pill issue">{issueLabel(j.issueCategory)}</span>
                      </div>
                      <code className="small text-muted">{j.jobNumber}</code>
                    </div>
                    <div className="text-end">
                      <div className="job-amount">{formatINR(j.finalCost || j.estimatedCost)}</div>
                      <small className="text-muted">{new Date(j.receivedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</small>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="table-responsive" style={{ maxHeight: 520, overflowY: 'auto' }}>
              <table className="table table-sm table-hover align-middle mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Job</th>
                    <th>Customer</th>
                    <th>Device</th>
                    <th>Status</th>
                    <th>₹</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j._id}>
                      <td><code className="small">{j.jobNumber}</code><br /><small className="text-muted">{new Date(j.receivedAt).toLocaleDateString('en-IN')}</small></td>
                      <td>{j.customerName}<br /><small>{j.customerPhone}</small></td>
                      <td>{j.deviceModelNormalized || [j.deviceBrand, j.deviceModel].filter(Boolean).join(' ') || '—'}</td>
                      <td><span className={`badge ${STATUS_BADGE[j.status] || 'bg-secondary'}`}>{displayStatus(j.status)}</span></td>
                      <td>{formatINR(j.finalCost || j.estimatedCost)}</td>
                      <td><button type="button" className="btn btn-outline-primary btn-sm" onClick={() => openDetail(j)}>Open</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className={`container-fluid py-3 ${mobileApp ? 'repair-mobile-app' : ''}`}>
      {toast && (
        <div className={`alert alert-${toast.type} alert-dismissible fade show position-fixed shadow ${mobileApp ? 'repair-toast' : ''}`} style={mobileApp ? { zIndex: 2000 } : { zIndex: 2000, maxWidth: 420, top: 0, end: 0, margin: '1rem' }}>
          {toast.msg}
          <button type="button" className="btn-close" onClick={() => setToast(null)} />
        </div>
      )}

      {!mobileApp && (
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <div>
            <h4 className="mb-0"><i className="bi bi-tools me-2" />Repair Business</h4>
            <small className="text-muted">Punjab (PB001) — customer database + WhatsApp alerts</small>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {readyCount > 0 && (
              <span className="badge bg-success fs-6">{readyCount} ready for pickup</span>
            )}
            <Link to="/repair-app" className="btn btn-sm btn-outline-primary">
              <i className="bi bi-phone me-1" />Mobile app
            </Link>
          </div>
        </div>
      )}

      {mobileApp ? (
        mobileTab === 'home' ? mobileHome : mobileTab === 'new' ? mobileIntakeForm : jobsList
      ) : (
        <>
          <div className="row g-2 mb-3">
            {[
              { label: 'Active jobs', value: stats?.activeJobs ?? '—', icon: 'bi-wrench' },
              { label: 'Ready pickup', value: stats?.readyForPickup ?? '—', icon: 'bi-bell', warn: readyCount > 0 },
              { label: 'Delivered (month)', value: stats?.deliveredThisMonth ?? '—', icon: 'bi-check-circle' },
              { label: 'Avg days', value: stats?.avgTurnaroundDays ?? '—', icon: 'bi-clock' },
            ].map((c) => (
              <div key={c.label} className="col-6 col-md-4 col-lg">
                <div className={`card h-100 ${c.warn ? 'border-success' : ''}`}>
                  <div className="card-body py-2 px-3">
                    <div className="text-muted small"><i className={`bi ${c.icon} me-1`} />{c.label}</div>
                    <div className="fs-5 fw-bold">{c.value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="row g-3">
            {intakeForm}
            {jobsList}
          </div>
        </>
      )}

      {mobileApp && (
        <nav className="repair-app-bottomnav" aria-label="Repair counter">
          <button type="button" className={mobileTab === 'home' ? 'active' : ''} onClick={() => setMobileTab('home')}>
            <i className="bi bi-house" />
            Home
          </button>
          <button type="button" className={`nav-fab ${mobileTab === 'new' ? 'active' : ''}`} onClick={() => setMobileTab('new')}>
            <span className="nav-fab-inner"><i className="bi bi-plus-lg" /></span>
            <span style={{ fontSize: '0.65rem', marginTop: 2 }}>Naya</span>
          </button>
          <button type="button" className={mobileTab === 'jobs' ? 'active' : ''} onClick={() => setMobileTab('jobs')}>
            <i className="bi bi-list-ul" />
            Jobs
            {readyCount > 0 && <span className="repair-nav-badge">{readyCount}</span>}
          </button>
        </nav>
      )}

      {/* Detail modal */}
      {selected && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.45)', zIndex: mobileApp ? 2000 : undefined }}>
          <div className={`modal-dialog modal-lg modal-dialog-scrollable ${mobileApp ? 'modal-fullscreen' : ''}`}>
            <div className="modal-content">
              <div className="modal-header py-2">
                <div>
                  <h5 className="modal-title mb-0">{selected.customerName}</h5>
                  <small className={mobileApp ? 'text-white-50' : 'text-muted'}>{selected.jobNumber}</small>
                </div>
                <button type="button" className="btn-close" onClick={() => setSelected(null)} />
              </div>
              <div className="modal-body">
                {mobileApp ? (
                  <div className="repair-detail-hero">
                    <div className="repair-detail-device">{[selected.deviceBrand, selected.deviceModel].filter(Boolean).join(' ') || 'Device'}</div>
                    <div className="small text-muted mt-1">{selected.customerPhone}</div>
                    <div className="job-meta mt-2">
                      <span className={`repair-job-pill status ${selected.status === 'ready' ? 'status-ready' : ['in_progress', 'waiting_parts', 'diagnosing'].includes(selected.status) ? 'status-repairing' : ''}`}>
                        {displayStatus(selected.status)}
                      </span>
                      <span className="repair-job-pill issue">{issueLabel(selected.issueCategory)}</span>
                    </div>
                    {selected.issueDescription && <div className="small mt-2">{selected.issueDescription}</div>}
                  </div>
                ) : (
                  <>
                    <p className="mb-2"><strong>{[selected.deviceBrand, selected.deviceModel].filter(Boolean).join(' ')}</strong> · {selected.issueDescription || selected.issueCategory}</p>
                    <p className="small text-muted mb-3">
                      Phone: {selected.customerPhone}
                      {' '}· <Link to={`/customer/${selected.customerId}`}>Customer profile</Link>
                    </p>
                  </>
                )}

                <div className={`${mobileApp ? 'repair-action-stack' : 'd-flex flex-wrap gap-1'} mb-3`}>
                  {(mobileApp ? ['in_progress', 'cancelled'] : ['diagnosing', 'in_progress', 'cancelled']).map((st) => (
                    <button
                      key={st}
                      type="button"
                      className={`btn ${mobileApp ? 'btn-lg w-100' : 'btn-sm'} ${
                        (st === 'in_progress' && ['in_progress', 'waiting_parts', 'diagnosing'].includes(selected.status)) || selected.status === st
                          ? 'btn-dark'
                          : 'btn-outline-secondary'
                      }`}
                      disabled={
                        updating
                        || selected.status === 'delivered'
                        || (st === 'cancelled' && selected.status === 'cancelled')
                        || (st === 'in_progress' && ['in_progress', 'waiting_parts', 'diagnosing'].includes(selected.status))
                      }
                      onClick={() => updateJob({ status: st, statusNote: `Marked ${st === 'in_progress' ? 'in repair' : st}` })}
                    >
                      {st === 'in_progress' ? 'In repair' : STATUS_LABELS[st]}
                    </button>
                  ))}
                </div>

                {selected.costFinalized && (
                  <div className="alert alert-secondary py-2 small mb-3">
                    <strong>Finish ho chuka:</strong> Parts ₹{Number(selected.partsCost || 0).toLocaleString('en-IN')} ·
                    Labour ₹{Number(selected.laborCost || 0).toLocaleString('en-IN')}
                    {selected.laborTierApplied ? ` (${selected.laborTierApplied})` : ''} ·
                    Customer ₹{Number(selected.finalCost || 0).toLocaleString('en-IN')}
                    {selected.partsUsed?.length > 0 && (
                      <ul className="mb-0 mt-1 ps-3">
                        {selected.partsUsed.map((p, i) => (
                          <li key={i}>{p.partName} ×{p.quantity}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                {!selected.costFinalized && selected.status !== 'cancelled' && selected.status !== 'delivered' && (
                  <button type="button" className={`btn btn-success mb-3 ${mobileApp ? 'btn-lg w-100' : 'btn-sm'}`} onClick={openFinishModal}>
                    <i className="bi bi-check2-circle me-1" />Finish repair
                  </button>
                )}

                {selected.costFinalized && selected.status === 'ready' && (
                  <button type="button" className={`btn btn-dark mb-3 ${mobileApp ? 'btn-lg w-100' : 'btn-sm'}`} onClick={submitDeliver} disabled={updating}>
                    <i className="bi bi-bag-check me-1" />Customer pickup — Deliver
                  </button>
                )}

                <div className="repair-section-card">
                  <div className="repair-section-head"><i className="bi bi-journal-text" /> Notes</div>
                  <div className="repair-section-body">
                    <div className="row g-2 mb-2">
                      <div className="col-6">
                        <label className="form-label small">Advance ₹</label>
                        <input className="form-control form-control-sm" type="number" value={editAdvance} onChange={(e) => setEditAdvance(e.target.value)} disabled={selected.costFinalized} />
                      </div>
                      <div className="col-6 d-flex align-items-end">
                        <button type="button" className="btn btn-sm btn-outline-secondary w-100" onClick={saveNotes} disabled={updating}>Save</button>
                      </div>
                    </div>
                    <div className="mb-0">
                      <label className="form-label small">Internal notes</label>
                      <textarea className="form-control form-control-sm" rows={2} value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                    </div>
                  </div>
                </div>

                {!mobileApp && (
                  <p className="small text-muted mb-0 mt-2">
                    Profit sirf <Link to="/repair-analysis">Business Analysis → Repair</Link> par dikhega.
                  </p>
                )}
              </div>
              <div className="modal-footer py-2">
                <button type="button" className={`btn btn-secondary ${mobileApp ? 'w-100' : 'btn-sm'}`} onClick={() => setSelected(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Finish repair modal */}
      {selected && showFinish && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 2100 }}>
          <div className={`modal-dialog modal-lg ${mobileApp ? 'modal-fullscreen' : ''}`}>
            <div className="modal-content">
              <div className="modal-header py-2 bg-success text-white">
                <h5 className="modal-title">Finish repair — {selected.jobNumber}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowFinish(false)} />
              </div>
              <div className="modal-body">
                <p className="small mb-2">
                  <span className="badge bg-secondary me-1">{finishPreview?.deviceTierLabel || 'Standard'}</span>
                  Problem: <strong>{ISSUE_OPTIONS.find((o) => o.value === selected.issueCategory)?.label || selected.issueCategory}</strong>
                  {!finishPreview?.allowManualLabor && (
                    <>
                      {' · '}
                      Labour: <strong>₹{finishPreview?.laborCost ?? '—'}</strong>{' '}
                      <Link to="/repair-analysis" onClick={() => setShowFinish(false)}>edit rates</Link>
                    </>
                  )}
                </p>

                {finishPreview?.allowManualLabor && (
                  <div className="mb-3">
                    <label className="form-label small">Labour ₹ * (Other problem — manually)</label>
                    <input
                      type="number"
                      min="1"
                      className="form-control form-control-sm"
                      value={finishManualLabor}
                      onChange={(e) => setFinishManualLabor(e.target.value)}
                      placeholder="Repairman cost likho"
                    />
                  </div>
                )}

                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label small">Customer se Final ₹ *</label>
                    <input type="number" min="1" className="form-control" value={finishFinal} onChange={(e) => setFinishFinal(e.target.value)} required />
                  </div>
                  <div className="col-6">
                    <label className="form-label small">Advance (optional)</label>
                    <input type="number" min="0" className="form-control" value={finishAdvance} onChange={(e) => setFinishAdvance(e.target.value)} />
                  </div>
                </div>

                {finishPreview?.partsRequired !== false ? (
                  <>
                    {suggestedParts.length > 0 && (
                      <div className="mb-2">
                        <label className="form-label small mb-1">Suggested for {selected.deviceModel}</label>
                        <div className="d-flex flex-wrap gap-1">
                          {suggestedParts.map((p) => (
                            <button
                              key={p._id}
                              type="button"
                              className="btn btn-sm btn-outline-success py-0"
                              onClick={() => pickSuggestedPart(p._id)}
                            >
                              + {p.name} ({p.quantity})
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="mb-2">
                      <input
                        className="form-control form-control-sm"
                        placeholder="Search parts…"
                        value={partSearch}
                        onChange={(e) => {
                          setPartSearch(e.target.value);
                          reloadPartsSearch(e.target.value);
                        }}
                      />
                    </div>
                    <label className="form-label small">Parts used (inventory — stock auto cut)</label>
                    {finishPartLines.map((line, idx) => (
                      <div key={idx} className="row g-2 mb-2 align-items-end">
                        <div className="col-8">
                          <select
                            className="form-select form-select-sm"
                            value={line.partId}
                            onChange={(e) => {
                              const next = [...finishPartLines];
                              next[idx] = { ...next[idx], partId: e.target.value };
                              setFinishPartLines(next);
                            }}
                          >
                            <option value="">— Part choose karo —</option>
                            {filteredParts.map((p) => (
                              <option key={p._id} value={p._id} disabled={p.quantity < 1}>
                                {p.suggested ? '★ ' : ''}{p.name} (stock {p.quantity}) ₹{p.unitCost}
                                {p.compatibleModels ? ` · ${p.compatibleModels.slice(0, 30)}` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-2">
                          <input
                            type="number"
                            min="1"
                            className="form-control form-control-sm"
                            value={line.quantity}
                            onChange={(e) => {
                              const next = [...finishPartLines];
                              next[idx] = { ...next[idx], quantity: e.target.value };
                              setFinishPartLines(next);
                            }}
                          />
                        </div>
                        <div className="col-2">
                          {idx === finishPartLines.length - 1 ? (
                            <button type="button" className="btn btn-sm btn-outline-primary w-100" onClick={() => setFinishPartLines([...finishPartLines, { partId: '', quantity: 1 }])}>+</button>
                          ) : (
                            <button type="button" className="btn btn-sm btn-outline-danger w-100" onClick={() => setFinishPartLines(finishPartLines.filter((_, i) => i !== idx))}>×</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                ) : (
                  <p className="small text-info">Software repair — parts select karne ki zaroorat nahi.</p>
                )}

                {!inventoryParts.length && finishPreview?.partsRequired !== false && (
                  <p className="small text-warning">Pehle <Link to="/repair-analysis">Analysis → Parts inventory</Link> me parts add karo.</p>
                )}
              </div>
              <div className="modal-footer py-2">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowFinish(false)}>Cancel</button>
                <button type="button" className="btn btn-success btn-sm" onClick={submitFinish} disabled={finishing}>
                  {finishing ? 'Saving…' : 'Finish + cut stock'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RepairsDashboard;
